import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { collectionGroup, collection, getDoc, getDocs } from 'firebase/firestore';
import { firestore } from '../services/firebase';
import { useCategories } from './useCategories';
import { hasProductAccess } from '../utils/accessUtils';
import { matchesSearchTitle, parseSearchWords } from '../utils/searchMatch';
import { useCatalogAccessVisibility } from './useCatalogAccessVisibility';

const SEARCH_VIDEOS_KEY = 'searchVideos';

const toSearchCardItem = (category, card) => ({
  ...card,
  id: card.id,
  cardId: card.id,
  categoryId: category.id,
  title: card.title || '',
  backgroundImage: card.backgroundImage || (card.img ? { url: card.img } : undefined),
});

const toSearchVideoItem = (videoDoc, categoryId, cardId, cardData, user) => {
  const data = videoDoc.data();
  const isLocked = cardData ? !hasProductAccess(cardData, user) : true;
  return {
    categoryId,
    cardId,
    videoId: videoDoc.id,
    id: videoDoc.id,
    title: data?.title || '',
    backgroundImage: { url: data?.backgroundImage?.url || data?.image },
    isLocked,
    ...(cardData || {}),
  };
};

async function fetchSearchVideos(term, scopedKeys, user, restrictToUserAccess = false) {
  const searchWords = parseSearchWords(term);
  if (searchWords.length === 0) return [];
  if (scopedKeys && scopedKeys.size === 0) return [];

  const firstWord = searchWords[0];
  const useScopedCardQueries =
    scopedKeys && scopedKeys.size > 0 && scopedKeys.size <= 24;

  const videoDocs = useScopedCardQueries
    ? await loadVideosFromScopedCards(scopedKeys)
    : (await getDocs(collectionGroup(firestore, 'videos'))).docs;

  const titleMatched = videoDocs.filter((videoDoc) => {
    const titleLower = (videoDoc.data()?.title || '').toLowerCase();
    if (!titleLower.includes(firstWord)) return false;
    return matchesSearchTitle(titleLower, searchWords);
  });

  const cardCache = new Map();
  const categoryCache = new Map();

  const results = await Promise.all(
    titleMatched.map(async (videoDoc) => {
      const cardRef = videoDoc.ref.parent?.parent;
      const categoryRef = cardRef?.parent?.parent;
      if (!cardRef || !categoryRef) return null;

      const categoryId = categoryRef.id;
      const cardId = cardRef.id;
      const scopeKey = `${categoryId}:${cardId}`;

      if (scopedKeys && !scopedKeys.has(scopeKey)) return null;

      let cardData = cardCache.get(scopeKey);
      if (cardData === undefined) {
        const cardSnap = await getDoc(cardRef);
        if (!cardSnap.exists() || cardSnap.data()?.archived) {
          cardCache.set(scopeKey, null);
          return null;
        }

        let categoryData = categoryCache.get(categoryId);
        if (categoryData === undefined) {
          const categorySnap = await getDoc(categoryRef);
          categoryData =
            categorySnap.exists() && !categorySnap.data()?.archived
              ? categorySnap.data()
              : null;
          categoryCache.set(categoryId, categoryData);
        }
        if (!categoryData) {
          cardCache.set(scopeKey, null);
          return null;
        }

        cardData = { id: cardSnap.id, ...cardSnap.data() };
        cardCache.set(scopeKey, cardData);
      }

      if (!cardData) return null;
      if (restrictToUserAccess && !hasProductAccess(cardData, user)) return null;

      return toSearchVideoItem(videoDoc, categoryId, cardId, cardData, user);
    })
  );

  return results
    .filter(Boolean)
    .sort((a, b) => (a.title || '').localeCompare(b.title || '', 'pt-BR'));
}

async function loadVideosFromScopedCards(scopedKeys) {
  const pairs = [...scopedKeys].map((key) => {
    const [categoryId, cardId] = key.split(':');
    return { categoryId, cardId };
  });

  const nested = await Promise.all(
    pairs.map(async ({ categoryId, cardId }) => {
      const snap = await getDocs(
        collection(firestore, 'categories', categoryId, 'cards', cardId, 'videos')
      );
      return snap.docs;
    })
  );

  return nested.flat();
}

/**
 * Busca web alinhada ao mobile: cards vêm do cache do useCategories (Main já
 * pré-carrega); vídeos usam collectionGroup + getDoc só nos matches de título.
 */
export function useWebSearch(searchTerm, { allowedCardKeys, user } = {}) {
  const term = (searchTerm || '').trim();
  const enabled = term.length >= 3;
  const { restrictToUserAccess } = useCatalogAccessVisibility();

  const { data: categories = [], isFetching: loadingCategories } = useCategories();

  const scopedKeys = useMemo(() => {
    // An empty array is still a scope (product with no visible modules yet):
    // returning null here searched the WHOLE catalog from inside a product.
    if (!Array.isArray(allowedCardKeys)) return null;
    return new Set(allowedCardKeys);
  }, [allowedCardKeys]);

  const scopeKey = scopedKeys ? [...scopedKeys].sort().join('|') : 'all';

  const cardsResults = useMemo(() => {
    if (!enabled) return [];

    const searchWords = parseSearchWords(term);
    const firstWord = searchWords[0];

    return categories
      .filter((category) => !category.archived)
      .flatMap((category) =>
        (category.cards || [])
          .filter((card) => !card.archived)
          .filter((card) => {
            const scopeId = `${category.id}:${card.id}`;
            return !scopedKeys || scopedKeys.has(scopeId);
          })
          .filter((card) => {
            const titleLower = (card.title || '').toLowerCase();
            if (!titleLower.includes(firstWord)) return false;
            return matchesSearchTitle(titleLower, searchWords);
          })
          .filter((card) => !restrictToUserAccess || hasProductAccess(card, user))
          .map((card) => toSearchCardItem(category, card))
      )
      .sort((a, b) => (a.title || '').localeCompare(b.title || '', 'pt-BR'));
  }, [categories, enabled, scopedKeys, term, restrictToUserAccess, user]);

  const { data: videosResults = [], isFetching: loadingVideos } = useQuery({
    queryKey: [SEARCH_VIDEOS_KEY, term, scopeKey, restrictToUserAccess],
    queryFn: () => fetchSearchVideos(term, scopedKeys, user, restrictToUserAccess),
    enabled,
    staleTime: 60 * 1000,
  });

  return {
    cardsResults,
    videosResults,
    loading: enabled && (loadingCategories || loadingVideos),
    searchPerformed: enabled,
  };
}
